<script setup lang="ts">
import type { PropertySearchCriteria } from '@@/shared/property/search-criteria';
import { AlertBodySchema, type AlertBody } from '@@/shared/alert/types';

const props = defineProps<{
  propertySearchCriteria: PropertySearchCriteria
}>();

const isOpen = defineModel<boolean>({ default: false });

const { locale } = useI18n();

const { mutate, data, error, isPending } = useCreateAlert();

const { handleSubmit, resetForm } = useForm({
  initialValues: {
    email: '',
    propertySearchCriteria: props.propertySearchCriteria,
    locale: locale.value,
    formattedSearchCriteria: '',
  },
  validationSchema: toTypedSchema(AlertBodySchema),
});
const { errorMessage: errorEmail, value: email } = useField<string>(`email`);
const { formattedSearchCriteria } = useAlertFormattedSearchCriteria(toRef(() => props.propertySearchCriteria));

const onSubmit = handleSubmit((values: AlertBody) => {
  isOpen.value = false;
  mutate({
    ...values,
    propertySearchCriteria: props.propertySearchCriteria,
    formattedSearchCriteria: formattedSearchCriteria.value,
  });
}, () => {
  if (errorEmail.value === 'core.form.error.mandatory_field') notifyError(t('core.form.error.enter_mandatory_fields'));
  else notifyError(t('core.form.error.correct_errors'));
});

const { t } = useI18n();
const { notifySuccess, notifyError } = useNotifications();
watch(data, () => {
  if (!data.value) return;
  trackEvent({
    event: 'alert_interaction',
    event_name: 'create_alert',
    alert_id: data.value.managementToken,
    h_email: email.value,
  });
  resetForm();
  notifySuccess(t(`property_serp.alert_dialog.notify_success`));
});
watch(error, () => {
  if (!error.value) return;
  resetForm();
  notifyError(t(`property_serp.alert_dialog.notify_error`));
});
</script>

<template>
  <IadDialog
    v-model="isOpen"
    modal-name="alert"
  >
    <template #top-content>
      <IadTitle
        tag="p"
        :text="t('property_serp.alert_dialog.title')"
        class="mx-auto w-screen lg:max-w-lg"
        variant="medium"
      />
    </template>
    <div class="flex flex-col gap-l px-m pb-m lg:px-0 w-screen mx-auto lg:max-w-lg">
      <AlertKeyPoints />
      <form
        id="alert-form"
        novalidate
        autocomplete="off"
        class="flex flex-col gap-l"
        @submit="onSubmit"
      >
        <div>
          <IadTitle
            :text="t(`property_serp.alert_dialog.label_email`)"
            tag="p"
            variant="small-semibold"
            class="pb-s"
          />
          <!-- TODO: we should have a way to have the error message appeared on position absolute -->
          <!-- This will enable removing the negative margin -->
          <IadInputText
            v-model="email"
            name="alert-email"
            placeholder="exemple@mail.com"
            :error="errorEmail ? $t(errorEmail) : ''"
          />
          <AlertDialogCriteria :formatted-search-criteria />
        </div>
        <IadBtn
          type="submit"
          size="large"
          :disabled="isPending"
          :loading="isPending"
          :text="t(`property_serp.alert_dialog.create_cta`)"
        />
      </form>
      <div>
        <p
          class="text-sm text-neutral-40"
          v-text="t(`core.common.required_field`)"
        />
        <I18nT
          keypath="property_serp.alert_dialog.legal_mentions"
          class="whitespace-pre-line text-xs text-neutral-40"
          tag="p"
        >
          <template #privacy_policy>
            <NuxtLinkLocale
              to="privacy-policy"
              class="lowercase underline"
              :text="$t(`core.navigation.privacy_policy`)"
            />
          </template>
        </I18nT>
      </div>
    </div>
  </IadDialog>
</template>
